// native
import { Component, OnInit, ChangeDetectionStrategy, ChangeDetectorRef, ElementRef, ViewChild, Input } from '@angular/core';
import { UntypedFormBuilder, UntypedFormGroup, Validators } from '@angular/forms';
import { Subscription } from 'rxjs';

// service
import { ActiveReferencesService } from '../../../core/services/active-references.service';
import { ItemsSyncService } from '../../../core/services/items-sync.service';
import { ItemsDisplayService } from '../../../core/services/items-display.service';
import { ToastService } from '../../../core/services/toast.service';
import { CitationsEventService } from '../../../core/services/citations-event.service';
import { ListService } from '../../../core/services/list.service';
import { GlobalsService } from '../../../core/services/globals.service';

// models
import { DocumentItemsResponse, IDisplayItem, DocumentCitations, ICollection, ListCreationPayload, ICitationSelection } from '../../../models';


@Component({
  selector: 'scs-active-references',
  templateUrl: './active-references.component.html',
  changeDetection: ChangeDetectionStrategy.OnPush,
  host: {
    '(document:click)': 'onOutsideDropdownClick($event)'
  }
})
export class ActiveReferencesComponent implements OnInit {

  @ViewChild('collectionBox') collectionBoxElement: ElementRef;
  @Input() sharedCollections: ICollection[];
  @Input() personalCollection: ICollection;
  @Input() isCitationDrawerVisible: boolean;

  isCollectionDropdownVisible: boolean = false;

  citationSelectedSubscription: Subscription;

  items: IDisplayItem[] = [];
  form: UntypedFormGroup;
  fetchReferencesLoading: boolean = false;

  constructor(
    public activeReferencesService: ActiveReferencesService,
    private citationsEventService: CitationsEventService,
    private itemsSyncService: ItemsSyncService,
    public itemsDisplayService: ItemsDisplayService,
    private changeDetectorRef: ChangeDetectorRef,
    private formBuilder: UntypedFormBuilder,
    private toastService: ToastService,
    private listService: ListService,
    private globalsService: GlobalsService
  ) { }

  ngOnInit(): void {
    this.fetchReferencesLoading = true;

    this.initCitationSubscriptions();

    this.citationsEventService.getDocumentCitations().then((citations: DocumentCitations) => {
      if (!citations.ids.length) {
        this.fetchReferencesLoading = false;
        this.changeDetectorRef.markForCheck();
        return;
      }

      this.itemsSyncService.getItemsByDocumentCitations(citations).then((response: DocumentItemsResponse) => {
        this.items = response.items;

        const nullItemsCount = response.nullItemsCount;

        if (nullItemsCount && nullItemsCount > 0) {
          this.toastService.openToast({
            text: `This document contains reference${nullItemsCount > 1 ? 's' : ''} to ${nullItemsCount} paper${nullItemsCount > 1 ? 's' : ''} 
                  that ${nullItemsCount > 1 ? 'have' : 'has'} been removed from user library. ${nullItemsCount > 1 ? 'Those' : 'That'} reference${nullItemsCount > 1 ? 's' : ''} 
                  ${nullItemsCount > 1 ? 'are' : 'is'} not displayed on active references list.`,
            type: 'error',
            autoClose: false
          });
        }

        this.fetchReferencesLoading = false;

        this.changeDetectorRef.markForCheck();
      }).catch(err => {
        this.fetchReferencesLoading = false;
        this.toastService.showGenericError();
      });
    }).catch(err => {
      this.fetchReferencesLoading = false;
      this.toastService.showGenericError();
    });

    this.initForm();
  }

  ngOnDestroy() {
    this.citationSelectedSubscription?.unsubscribe();
  }

  initForm() {
    let listName = '';
    this.form = this.formBuilder.group({
      listName: [listName, [Validators.required, Validators.maxLength(100)]],
    });
  }

  toggleShowAuthors(item: IDisplayItem) {
    item.article.isFullAuthorsShown = !item.article.isFullAuthorsShown;
  }

  onClose(reload = false) {
    this.activeReferencesService.areActiveReferencesShown = false;

    if (reload)
      this.listService.reload();
  }

  createList(payload: ListCreationPayload) {
    this.form.markAllAsTouched();

    if (this.form.invalid)
      return;

    const listName = this.form.controls.listName.value;

    // timeout because of edge windows reloading the app bug
    setTimeout(() => {
      this.activeReferencesService.createListLoading$.next(true);
      this.changeDetectorRef.markForCheck();
    });

    this.activeReferencesService.addList(this.items, listName, payload.collectionId, payload.parentListId).subscribe(lists => {
      this.activeReferencesService.createListLoading$.next(false);

      this.toastService.openToast({
        text: `List <i>${(lists && lists[0]?.name) ? lists[0].name : ''}</i> with active references has been created. Click 
          <a href="${this.globalsService.appConfig.environment.baseUrls.webapp}/library/${lists[0].collection_id}/list/${lists[0].id}" 
          target="_blank">here</a> to view the created list.`,
        type: 'success',
        autoClose: false
      });
      this.onClose(true);
    }, err => {
      this.activeReferencesService.createListLoading$.next(false);

      this.toastService.showGenericError();
      this.onClose(true);
    });
  }

  onCopyBibTex() {
    this.activeReferencesService.copyBibTexToClipboard(this.items);
  }

  onCopyRis() {
    this.activeReferencesService.copyRisToClipboard(this.items);
  }

  openCollectionPopup() {
    this.isCollectionDropdownVisible = true;
  }

  onOutsideDropdownClick(event) {
    if (!this.collectionBoxElement?.nativeElement.contains(event.target)) {
      this.isCollectionDropdownVisible = false;
    }
  }

  private initCitationSubscriptions(): void {
    this.citationSelectedSubscription = this.citationsEventService.citationSelected$.subscribe((selection: ICitationSelection) => {
      if (selection.isCitationDeselected) {
        this.uncheckAllItems();
        this.changeDetectorRef.markForCheck();
      }
    });
  }

  private uncheckAllItems() {
    this.items?.length && this.items.forEach((item: IDisplayItem) => {
      item.isChecked = false;
    });
  }

  onItemChecked(item: IDisplayItem, event: MouseEvent) {
    item.isChecked = !item.isChecked;

    if (item.isChecked) {
      this.citationsEventService.itemAddedToCitationQueue$.next(item);
      return;
    }

    this.citationsEventService.itemUncheckedFromList$.next(item);
  }
}
